import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { AnyNetworkElement, Abgangsleitung, NetworkElementType, Hausanschluss, LoadProfileType } from '../types/network';
import { loadNetworkData, updateNetworkElement } from '../services/networkDataService';
import { networkUpdatesService } from '../services/networkUpdatesService';
import { PendingChangesResult, changeTrackingService } from '../services/changeTrackingService';
import { useZoneStore } from './zoneStore';
import type { LoadInputSource } from '../utils/meterProvenance';

function isValidNetworkElementType(type: string): type is NetworkElementType {
  const validTypes = ['trafostation', 'abgangsleitung', 'kabelverteilschrank', 'hausanschluss', 'muffe'];
  return validTypes.includes(type.toLowerCase());
}

// Add a method to update power calculations from backend polling

// Validate connections between elements
function validateConnections(elements: AnyNetworkElement[]): AnyNetworkElement[] {
  const elementIds = new Set(elements.map(e => e.id));
  
  return elements.map(e => {
    const element = { ...e };
    
    if (element.type === 'muffe') {
      // Prüfe ob die verbundenen Leitungen noch existieren
      if (element.connectedLines) {
        element.connectedLines = element.connectedLines.filter(connectedLine => {
          const isValid = elementIds.has(connectedLine.id);
          return isValid;
        });
      }
    }
    else if (element.type === 'hausanschluss') {
      // Prüfe ob die verbundene Leitung noch existiert
      if (element.connectedLine?.id && !elementIds.has(element.connectedLine.id)) {
    
        element.connectedLine = undefined;
      }
    }
    else if (element.type === 'abgangsleitung') {
      // Prüfe ob source/target noch existieren
      if (element.sourceId && !elementIds.has(element.sourceId)) {
        element.sourceId = undefined;
      }
      if (element.targetId && !elementIds.has(element.targetId)) {
        element.targetId = undefined;
      }
      
    }
    
    return element;
  });
}

function validateAndTransformElement(element: unknown): AnyNetworkElement | null {
  if (!element || typeof element !== 'object') {
    return null;
  }

  let obj = element as Record<string, any>;
  
  if (!obj.type) {
    return null;
  }

  if (!obj.id) {
    return null;
  }

  // Allow empty names for Hausanschluss elements (they always have coordinates for display)
  // and for other element types that have address info
  if (!obj.name && obj.type !== 'hausanschluss' && (!obj.street && !obj.city)) {
    return null;
  }

  // Normalize type to lowercase
  obj.type = obj.type.toLowerCase();

  if (!isValidNetworkElementType(obj.type)) {
    return null;
  }

  // Ensure basic required fields
  obj.id = obj.id || crypto.randomUUID();
  // Hausanschluss always passes; set default name only for non-Hausanschluss types
  if (obj.type !== 'hausanschluss') {
    obj.name = obj.name || `New ${obj.type}`;
  }
  obj.description = obj.description || obj.name || '';
  obj.metadata = obj.metadata || {};
  obj.switchedOn = obj.switchedOn ?? true;

  // Handle coordinates based on type
  if (obj.type !== 'abgangsleitung') {
    if (!obj.coordinates) {
      obj.coordinates = { lat: 0, lng: 0 }; // Default coordinates instead of rejecting
    } else if (Array.isArray(obj.coordinates)) {
      
      // Convert array coordinates to object format
      obj.coordinates = {
        lat: obj.coordinates[1],
        lng: obj.coordinates[0]
      };
    }
  }

  // Type-specific validation and defaults
  switch (obj.type) {
    case 'hausanschluss':
      {

        // Cast to Hausanschluss to ensure type safety
        const hausanschluss = obj as Hausanschluss;
        
        // Initialize required arrays
        hausanschluss.smgws = hausanschluss.smgws || [];
        hausanschluss.technischeRessourcen = hausanschluss.technischeRessourcen || [];
        hausanschluss.steuerboxes = hausanschluss.steuerboxes || [];
        // Ensure loadProfile has a value and is uppercase
        if (!hausanschluss.loadProfile) {
          hausanschluss.loadProfile = 'H0';
        } else {
          hausanschluss.loadProfile = hausanschluss.loadProfile.toUpperCase() as LoadProfileType;
        }

        // Set default values for new properties
        hausanschluss.connectionKind = hausanschluss.connectionKind || 'household';
        hausanschluss.annualConsumption = hausanschluss.annualConsumption || 3600;
        hausanschluss.connectionPower = hausanschluss.connectionPower || 44;
        hausanschluss.street = hausanschluss.street || null;
        hausanschluss.houseNumber = hausanschluss.houseNumber || null;
        hausanschluss.city = hausanschluss.city || null;
        
        // Initialize connectedLine properties if it exists
        if (hausanschluss.connectedLine) {
          hausanschluss.connectedLine.isEnabled = hausanschluss.connectedLine.isEnabled ?? true;
          hausanschluss.connectedLine.totalPower = hausanschluss.connectedLine.totalPower ?? 0;
        }

        obj = hausanschluss as AnyNetworkElement;
      }
      break;

    case 'trafostation':
    
      obj.voltage = obj.voltage || { primary: 20000, secondary: 400 };
      obj.capacity = obj.capacity || 630;
      obj.fuses = obj.fuses || {};
      obj.connectedLines = obj.connectedLines || [];
      // PandaPower properties
      obj.powerKW = obj.powerKW ?? null;
      obj.currentA = obj.currentA ?? null;
      obj.loadingPercent = obj.loadingPercent ?? null;
      
      // Ensure each connectedLine has all required properties
      obj.connectedLines = obj.connectedLines.map((line: any) => {
       
        // Check all possible property names
        const processedLine = {
          id: line.id || '',
          crossSection: line.crossSection || 150,
          MeLoID: line.MeLoID || line.meLoId || '',
          isEnabled: line.isEnabled ?? true,
          cableSegments: line.cableSegments || [{ id: '', index: 0, length: 0 }]
        };
        
       
        return processedLine;
      });
      
      break;

    case 'kabelverteilschrank':

      obj.fuses = obj.fuses || {};
      obj.connectedLines = obj.connectedLines || [];
      // Ensure each connected line has proper metadata structure
      obj.connectedLines = obj.connectedLines.map((line: any) => ({
        id: line.id || '',
        crossSection: line.crossSection || 150,
        MeLoID: line.MeLoID || line.meLoId || '',
        isEnabled: line.isEnabled ?? true,
        cableSegments: line.cableSegments || [{ id: '', index: 0, length: 0 }],
        metadata: line.metadata || {}
      }));
      break;

    case 'abgangsleitung':
      if (!obj.waypoints || !Array.isArray(obj.waypoints)) {
        obj.waypoints = [];
      } else {
        // Validate each waypoint has valid coordinates
        obj.waypoints = obj.waypoints.filter((wp: any) => {
          const isValid = wp &&
                        typeof wp === 'object' &&
                        typeof wp.lat === 'number' &&
                        !isNaN(wp.lat) &&
                        typeof wp.lng === 'number' &&
                        !isNaN(wp.lng);
          return isValid;
        });
      }
      obj.totalLength = obj.totalLength || 0;
      obj.segments = (!Array.isArray(obj.segments) ? [] : obj.segments)
        .map((segment: any) => {
          // Ensure segment has required properties
          return {
            ...segment,
            startCoordinate: segment.startCoordinate || { lat: 0, lng: 0 },
            endCoordinate: segment.endCoordinate || { lat: 0, lng: 0 },
            waypoints: Array.isArray(segment.waypoints) ? segment.waypoints : [],
            length: segment.length || 0,
            loadingPercent: segment.loadingPercent ?? null,
            currentA: segment.currentA ?? null,
            currentFromA: segment.currentFromA ?? null,
            currentToA: segment.currentToA ?? null,
            powerKW: segment.powerKW ?? null,
            powerKVAR: segment.powerKVAR ?? null,
            powerKVA: segment.powerKVA ?? null,
            fromBusVoltagePu: segment.fromBusVoltagePu ?? null,
            toBusVoltagePu: segment.toBusVoltagePu ?? null,
            fromBusVoltageKv: segment.fromBusVoltageKv ?? null,
            toBusVoltageKv: segment.toBusVoltageKv ?? null
          };
        });
      obj.isEnabled = obj.isEnabled ?? true;
      obj.cableType = obj.cableType || 'NAYY';
      obj.crossSection = obj.crossSection || 150;
      break;

    case 'muffe':
      if (!obj.connectedLines) {
        obj.connectedLines = [];
      }
      if (!Array.isArray(obj.connectedLines)) {
        obj.connectedLines = [];
      }
      // Ensure each connected line has proper metadata structure
      obj.connectedLines = obj.connectedLines.map((line: any) => ({
        id: line.id || '',
        crossSection: line.crossSection || 150,
        meLoId: line.meLoId || '',
        isEnabled: line.isEnabled ?? true,
        cableSegments: line.cableSegments || [{ id: '', index: 0, length: 0 }],
        MeLoID: line.MeLoID || line.meLoId || '',
        metadata: line.metadata || {}
      }));
      break;
  }

  return obj as AnyNetworkElement;
}

interface PowerMeasurements {
  [elementId: string]: {
    totalActivePower: number;
    timestamp: number;
    simValue: number | null;
    resultValue: number | null;
    phases: {
      L1: { voltage: number; current: number; power: number; powerFactor?: number };
      L2: { voltage: number; current: number; power: number; powerFactor?: number };
      L3: { voltage: number; current: number; power: number; powerFactor?: number };
    };
    voltageKV?: number | null;
    voltagePU?: number | null;
    measuredVoltageV?: number | null;
    measuredVoltagePu?: number | null;
    measuredVoltageReference?: string | null;
    measuredVoltageSource?: string | null;
    measuredVoltageTimestamp?: string | null;
    // TAF10 additional measurements
    powerFactorTotal?: number | null;
    frequencyHz?: number | null;
    voltageL1Pu?: number | null;
    voltageL2Pu?: number | null;
    voltageL3Pu?: number | null;
    voltageUnbalancePercent?: number | null;
    loadingPercent?: number | null;
    totalApparentPowerKva?: number | null;
    totalReactivePowerKvar?: number | null;
    /**
     * RAW per-phase solver values (null when the result carries none — e.g.
     * single-phase solves or archives written before 2026-08). `phases.*`
     * above always holds a value because it falls back to totals/3 and an
     * estimated current; use these when a display must not show fallbacks.
     */
    powerL1Kw?: number | null;
    powerL2Kw?: number | null;
    powerL3Kw?: number | null;
    currentL1A?: number | null;
    currentL2A?: number | null;
    currentL3A?: number | null;
    /**
     * Hausanschluss only: what drove this load-flow value — see `LoadInputSource`
     * (`utils/meterProvenance.ts`). Null on archive frames and older pools.
     */
    inputSource?: LoadInputSource | null;
  };
}

interface LineSegmentState {
  id: string;
  powerKW: number | null;
  powerKVAR: number | null;
  powerKVA?: number | null;
  currentA: number | null;
  currentFromA?: number | null;
  currentToA?: number | null;
  // Per-phase currents for 3-phase display
  currentL1A?: number | null;
  currentL2A?: number | null;
  currentL3A?: number | null;
  loadingPercent: number | null;
  fromBusVoltagePu?: number | null;
  toBusVoltagePu?: number | null;
  fromBusVoltageKv?: number | null;
  toBusVoltageKv?: number | null;
  name: string;
  fromBusElementId?: string | null;
  toBusElementId?: string | null;
  sourceElement?: string | null;
  targetElement?: string | null;
  length: number;
  /** Timestamp (epoch ms) of the last power flow calculation for this segment */
  calculatedAt?: number | null;
}

interface NetworkElementsState {
  elements: AnyNetworkElement[];
  segments: Record<string, LineSegmentState>;
  isLoading: boolean;
  loadingMessage: string;
  hasError: boolean;
  errorMessage: string;
  powerUpdateCounter: number;
  powerMeasurements: PowerMeasurements;
  trafoMeasurements: Record<string, import('../services/trafoMeasurementService').TrafoMeasurementData>;
  pendingImportChanges: PendingChangesResult | null;
}

interface NetworkElementsActions {
  loadElements: () => Promise<void>;
  updateElement: (id: string, element: Partial<AnyNetworkElement>) => Promise<{success: boolean; error?: string}>;
  toggleAbgangsleitungConnection: (id: string) => Promise<void>;
  toggleAbgangsleitungPower: (id: string) => Promise<void>;
  getConnectedAbgangsleitungen: (elementId: string) => Abgangsleitung[];
  getElement: (id: string) => AnyNetworkElement | undefined;
  updatePowerMeasurements: (measurements: PowerMeasurements) => void;
  clearPowerData: () => void;
  updateTrafoMeasurements: (measurements: Record<string, import('../services/trafoMeasurementService').TrafoMeasurementData>) => void;
  clearElements: () => void;
  setLoading: (loading: boolean) => void;
  setLoadingMessage: (message: string) => void;
  setElements: (elements: AnyNetworkElement[]) => void;
  incrementPowerUpdateCounter: () => void;
  // Error handling actions
  setError: (hasError: boolean, message?: string) => void;
  // New actions for segment management
  updateSegment: (segmentId: string, data: Partial<LineSegmentState>) => void;
  /** One store write for a whole power-calculation batch (see powerCalculationMapping). */
  updateSegments: (batch: Record<string, Partial<LineSegmentState>>) => void;
  getSegment: (segmentId: string) => LineSegmentState | undefined;
  updatePowerCalculations: (calculations: any[]) => void;
  // Change tracking actions
  setPendingImportChanges: (changes: PendingChangesResult | null) => void;
  applyPendingChanges: (changeIds: string[]) => Promise<void>;
  discardPendingChanges: (changeIds: string[]) => Promise<void>;
}

type NetworkElementsStore = NetworkElementsState & NetworkElementsActions;

/**
 * Keeps object identity for elements whose content did not change between two loads.
 * Both arrays come out of the same transform (same key order), so a JSON comparison is a
 * stable structural equality; ~1000 elements compare in a few milliseconds, far cheaper
 * than re-rendering every marker and cable because the array was rebuilt.
 */
const reuseUnchangedElements = (prev: AnyNetworkElement[], next: AnyNetworkElement[]): AnyNetworkElement[] => {
  if (prev.length === 0 || next.length === 0) return next;
  const byId = new Map<string, AnyNetworkElement>();
  for (const el of prev) byId.set(el.id, el);
  let reused = 0;
  const out = next.map(el => {
    const old = byId.get(el.id);
    if (old && JSON.stringify(old) === JSON.stringify(el)) {
      reused++;
      return old;
    }
    return el;
  });
  return reused === next.length && prev.length === next.length ? prev : out;
};

export const useNetworkElementsStore = create<NetworkElementsStore>()(
  devtools<NetworkElementsStore>((set, get) => {
    (window as any).getNetworkElements = () => get().elements;

    // Subscribe to SSE updates for real-time network element changes
    const unsubscribeCallbacks: Array<() => void> = [];

    // Debounced full reload: bulk operations (e.g. incremental import of 50
    // elements) emit one SSE event per element — without debouncing, each event
    // triggered its own full network fetch + validation + map re-render.
    let reloadTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleReload = (_reason: string) => {
      if (reloadTimer) clearTimeout(reloadTimer);
      reloadTimer = setTimeout(() => {
        reloadTimer = null;
        void get().loadElements();
      }, 500);
    };

    const subscribeToElementEvents = () => {
      unsubscribeCallbacks.push(
        networkUpdatesService.subscribe('element-update', (event) => {
          if (event.elementId) {
            scheduleReload(`element-update ${event.elementId}`);
          }
        })
      );
      unsubscribeCallbacks.push(
        networkUpdatesService.subscribe('element-delete', (event) => {
          if (event.elementId) {
            scheduleReload(`element-delete ${event.elementId}`);
          }
        })
      );
      unsubscribeCallbacks.push(
        networkUpdatesService.subscribe('element-create', (event) => {
          if (event.elementId) {
            scheduleReload(`element-create ${event.elementId}`);
          }
        })
      );
    };

    subscribeToElementEvents();

    const cleanupSubscriptions = () => {
      unsubscribeCallbacks.forEach(unsubscribe => unsubscribe());
      unsubscribeCallbacks.length = 0;
      if (reloadTimer) {
        clearTimeout(reloadTimer);
        reloadTimer = null;
      }
    };

    const resubscribeToSSE = () => {
      // Only subscribe if not already subscribed
      if (unsubscribeCallbacks.length > 0) return;
      subscribeToElementEvents();
    };

    // Expose for manual cleanup (dev tooling)
    if (typeof window !== 'undefined') {
      (window as any).cleanupNetworkUpdates = cleanupSubscriptions;
    }

      const clearElements = () => {
        // Unsubscribe from SSE element events to prevent stale zone listeners
        cleanupSubscriptions();
        set({
          elements: [],
          segments: {},
          powerMeasurements: {},
          trafoMeasurements: {},
          pendingImportChanges: null,
          powerUpdateCounter: 0,
        }, false, 'clearElements');
      };

      return {
        // State
        elements: [] as AnyNetworkElement[],
        segments: {},
        isLoading: false,
        loadingMessage: 'Lade...',
        hasError: false,
        errorMessage: '',
        updatePowerCalculations: (_calculations: any[]) => {
        },
        powerUpdateCounter: 0,
        powerMeasurements: {},
        trafoMeasurements: {},
        pendingImportChanges: null,

        // Error handling action
        setError: (hasError: boolean, message: string = '') => {
          set({ hasError, errorMessage: message }, false, 'setError');
        },

        // Actions
        updateSegment: (segmentId: string, data: Partial<LineSegmentState>) => {
          set(state => {
            const currentSegment = state.segments[segmentId];
            // Skip update if data hasn't changed
            if (currentSegment && Object.entries(data).every(([key, value]) =>
              currentSegment[key as keyof LineSegmentState] === value
            )) {
              return state;
            }

            const updatedSegment = {
              ...currentSegment,
              ...data
            };

            return {
              segments: {
                ...state.segments,
                [segmentId]: updatedSegment
              }
            };
          }, false, 'updateSegment');
        },

        // Batch variant: the per-segment updateSegment spread the whole segments record
        // once PER segment (O(N^2) copies) and notified every subscriber N times per
        // 10 s poll - with 2000 segments that was the dominant cost of a power update.
        // Unchanged segments keep their object identity so memoized cable components
        // can skip re-rendering.
        updateSegments: (batch: Record<string, Partial<LineSegmentState>>) => {
          set(state => {
            let next: Record<string, LineSegmentState> | null = null;
            for (const segmentId in batch) {
              const data = batch[segmentId];
              const current = state.segments[segmentId];
              if (current) {
                let changed = false;
                for (const key in data) {
                  if (current[key as keyof LineSegmentState] !== data[key as keyof LineSegmentState]) { changed = true; break; }
                }
                if (!changed) continue;
              }
              if (!next) next = { ...state.segments };
              next[segmentId] = { ...current, ...data } as LineSegmentState;
            }
            return next ? { segments: next } : state;
          }, false, 'updateSegments');
        },

        getSegment: (segmentId: string) => {
          return get().segments[segmentId];
        },

        // startPowerUpdates removed
        clearElements,
        setLoading: (loading: boolean) => set({
          isLoading: loading,
          // Reset error state when starting to load
          ...(loading ? { hasError: false, errorMessage: '' } : {})
        }, false, 'setLoading'),
        
        setLoadingMessage: (message: string) => set({ loadingMessage: message }, false, 'setLoadingMessage'),
        
        setElements: (elements: AnyNetworkElement[]) => set({
          elements,
          isLoading: false
        }, false, 'setElements'),
        incrementPowerUpdateCounter: () => set(
          state => ({ powerUpdateCounter: state.powerUpdateCounter + 1 }),
          false,
          'incrementPowerUpdateCounter'
        ),

      loadElements: async () => {
        try {
          // Capture active zone before async call to detect stale responses
          const zoneAtStart = useZoneStore.getState().activeZoneId;
          set({ isLoading: true });
          const data = await loadNetworkData();

          // If zone changed during the fetch, discard the stale result
          if (useZoneStore.getState().activeZoneId !== zoneAtStart) {
            set({ isLoading: false }, false, 'loadElements/stale');
            return;
          }
        
          
          // Transform and validate elements
          const transformedElements: AnyNetworkElement[] = [];
          const elements = data.elements || [];
          if (!Array.isArray(elements)) {
            set({
              isLoading: false,
              hasError: true,
              errorMessage: 'Invalid network data format'
            }, false, 'loadElements/error');
            return;
          }

          elements.forEach((element) => {
            const transformed = validateAndTransformElement(element);
            if (transformed) {
              transformedElements.push(transformed);
            }
          });

          // Always update the store with the latest data, even if empty. Elements whose
          // content did not change keep their object identity (see reuseUnchangedElements),
          // so the memoized markers/cables of a 1000-element zone skip re-rendering on the
          // SSE-triggered and details-panel reloads.
          const validatedElements = reuseUnchangedElements(get().elements, validateConnections(transformedElements));

          set(
            {
              elements: validatedElements,
              isLoading: false
            },
            false,
            'loadElements/success'
          );

          // Re-subscribe to SSE after successful load (subscriptions are cleared on zone switch)
          resubscribeToSSE();
        } catch (error) {
          // Set error state and clear elements
          set({
            isLoading: false,
            hasError: true,
            errorMessage: error instanceof Error ? error.message : 'Netzwerkdaten konnten nicht geladen werden'
          }, false, 'loadElements/error');
        }
      },

      updatePowerMeasurements: (measurements: PowerMeasurements) => {
        set(
          state => ({
            powerMeasurements: {
              ...state.powerMeasurements,
              ...measurements
            },
            powerUpdateCounter: state.powerUpdateCounter + 1
          }),
          false,
          'updatePowerMeasurements'
        );
      },

      updateTrafoMeasurements: (measurements) => {
        set({ trafoMeasurements: measurements }, false, 'updateTrafoMeasurements');
      },

      // Drops all power-flow-derived state (segments, power/trafo measurements)
      // WITHOUT touching the element list. updatePowerMeasurements merges, so a
      // mode switch (historical playback -> live) must clear explicitly — in
      // zones without a live cycle the previous frame would otherwise be shown
      // as if it were live data.
      clearPowerData: () => {
        set(
          state => ({
            segments: {},
            powerMeasurements: {},
            trafoMeasurements: {},
            powerUpdateCounter: state.powerUpdateCounter + 1
          }),
          false,
          'clearPowerData'
        );
      },

      updateElement: async (id: string, updatedElement: Partial<AnyNetworkElement>): Promise<{success: boolean; error?: string}> => {
        const currentState = get();
        if (currentState.isLoading) {
          return {success: false, error: 'Store is loading'};
        }

        const existingElement = currentState.elements.find(el => el.id === id);
        if (!existingElement) {
          return {success: false, error: 'Element not found'};
        }

        // Check if update is actually needed
        const hasChanges = Object.entries(updatedElement).some(([key, value]) =>
          existingElement[key as keyof AnyNetworkElement] !== value
        );

        if (!hasChanges) {
          return {success: true}; // No changes needed
        }

        // Create the merged element for validation
        const mergedElement = { ...existingElement, ...updatedElement };
        const validated = validateAndTransformElement(mergedElement);
        if (!validated) {
          console.error('[NetworkElements] Validation failed for element:', {
            elementId: id,
            elementType: existingElement.type,
            updates: updatedElement
          });
          return {success: false, error: 'Validation failed'};
        }

        // Update state optimistically
        const newElements = currentState.elements.map((element) =>
          element.id === id ? validated : element
        );
        const validatedElements = validateConnections(newElements);
        set({ elements: validatedElements }, false, 'updateElement');

        // Use single-element update endpoint - send complete merged element
        try {
          const result = await updateNetworkElement(id, validated);

          if (!result.success) {
            // Revert on failure
            await get().loadElements();
            return result;
          }

          return result;
        } catch (error) {
          console.error('[NetworkElements] Failed to update element:', error);
          // Reload elements to ensure consistency
          await get().loadElements();
          return {success: false, error: error instanceof Error ? error.message : 'Failed to update element'};
        }
      },

      toggleAbgangsleitungConnection: async (id: string) => {
        const currentState = get();
        const element = currentState.elements.find(el => el.id === id && el.type === 'abgangsleitung');
        if (!element) return;

        // Use the updateElement method which handles single-element updates
        const result = await get().updateElement(id, { switchedOn: !element.switchedOn });

        if (!result.success) {
          console.error('[NetworkElements] Failed to toggle connection:', result.error);
        }
      },

      toggleAbgangsleitungPower: async (id: string) => {
        const currentState = get();
        const element = currentState.elements.find(el => el.id === id && el.type === 'abgangsleitung');
        if (!element) return;

        // Use the updateElement method which handles single-element updates
        const result = await get().updateElement(id, { switchedOn: !element.switchedOn });

        if (!result.success) {
          console.error('[NetworkElements] Failed to toggle power:', result.error);
        }
      },

      getConnectedAbgangsleitungen: (elementId: string) => {
        const elements = get().elements;
        const connected = elements.filter(
          (el): el is Abgangsleitung =>
            el.type === 'abgangsleitung' &&
            (el.sourceId === elementId || el.targetId === elementId)
        );
        return connected;
      },

      getElement: (id: string) => {
        return get().elements.find((element) => element.id === id);
      },

      // Change tracking actions
      setPendingImportChanges: (changes: PendingChangesResult | null) => {
        set({ pendingImportChanges: changes }, false, 'setPendingImportChanges');
      },

      applyPendingChanges: async (changeIds: string[]) => {
        try {
          set({ isLoading: true, loadingMessage: 'Änderungen werden angewendet...' }, false, 'applyPendingChanges:start');

          await changeTrackingService.applyChanges(changeIds);

          // Reload elements to get the updated network
          await get().loadElements();

          // Clear pending changes
          set({ pendingImportChanges: null }, false, 'applyPendingChanges:clearPending');

        } catch (error) {
          console.error('[Store] Failed to apply changes:', error);
          set({
            hasError: true,
            errorMessage: error instanceof Error ? error.message : 'Fehler beim Anwenden der Änderungen'
          }, false, 'applyPendingChanges:error');
        } finally {
          set({ isLoading: false }, false, 'applyPendingChanges:end');
        }
      },

      discardPendingChanges: async (changeIds: string[]) => {
        try {
          set({ isLoading: true, loadingMessage: 'Änderungen werden verworfen...' }, false, 'discardPendingChanges:start');

          await changeTrackingService.discardChanges(changeIds);

          // Clear pending changes
          set({ pendingImportChanges: null }, false, 'discardPendingChanges:clearPending');

        } catch (error) {
          console.error('[Store] Failed to discard changes:', error);
          set({
            hasError: true,
            errorMessage: error instanceof Error ? error.message : 'Fehler beim Verwerfen der Änderungen'
          }, false, 'discardPendingChanges:error');
        } finally {
          set({ isLoading: false }, false, 'discardPendingChanges:end');
        }
      }
    };
  })
);
